import React, { useState, useEffect } from 'react';
import { useIntl, FormattedMessage } from 'react-intl';
import { useSuccessionTheme } from '../SuccessionThemeProvider';
import { useMutation } from '@apollo/client';
import { gql } from '@apollo/client';
import _ from 'lodash';
import { hidesListingGuidance } from './partialSalesFeatures';

const GENERATE_PARTIAL_SALE_AI_DESCRIPTION = gql`
  mutation generatePartialSaleAiDescription(
    $numberOfClients: Int!,
    $assetsUnderManagement: Float!,
    $feeBasedRevenue: Float!,
    $trailerRevenue: Float!,
    $directRevenue: Float!,
    $commissionRevenue: Float!,
    $states: [String!],
    $clientDemographicsDetails: String,
    $servicesProvidedDetails: String
  ) {
    generatePartialSaleAiDescription(
      numberOfClients: $numberOfClients
      assetsUnderManagement: $assetsUnderManagement
      feeBasedRevenue: $feeBasedRevenue
      trailerRevenue: $trailerRevenue
      directRevenue: $directRevenue
      commissionRevenue: $commissionRevenue
      states: $states
      clientDemographicsDetails: $clientDemographicsDetails
      servicesProvidedDetails: $servicesProvidedDetails
    ) {
      name
      description
    }
  }
`;

const Description = ({ formData, setFormData, user, company, onValidationChange }) => {
  const intl = useIntl();
  const { theme, isLightTheme, isDarkTheme } = useSuccessionTheme();
  const [generateAiDescription, { loading: aiLoading }] = useMutation(GENERATE_PARTIAL_SALE_AI_DESCRIPTION);
  const [errors, setErrors] = useState({});

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.segmentDetails || formData.segmentDetails.trim().length === 0) {
      newErrors.segmentDetails = intl.formatMessage({ id: 'succession.partial_sales.description.error_segment_name_required' });
    }

    if (!formData.description || formData.description.trim().length === 0) {
      newErrors.description = intl.formatMessage({ id: 'succession.partial_sales.description.error_description_required' });
    } else if (formData.description.length > 250) {
      newErrors.description = intl.formatMessage({ id: 'succession.partial_sales.description.error_description_max_length' });
    }

    setErrors(newErrors);
    const hasErrors = Object.keys(newErrors).length > 0;
    
    // Notify parent component about validation state
    if (onValidationChange) {
      onValidationChange(hasErrors);
    }
    
    return !hasErrors;
  };

  // Validate when form data changes
  useEffect(() => {
    validateForm();
  }, [formData.segmentDetails, formData.description]);

  // Initial validation on mount
  useEffect(() => {
    validateForm();
  }, []);

  const handleInputChange = (name, value) => {
    setFormData({
      ...formData,
      [name]: value
    });
    
    // Clear specific error when user starts typing
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: ''
      });
    }
  };

  const handleGenerateAiDescription = async () => {
    try {
      const { data } = await generateAiDescription({
        variables: {
          numberOfClients: parseInt(formData.numberOfClients) || 0,
          assetsUnderManagement: formData.assetsUnderManagement || 0,
          feeBasedRevenue: formData.feeBasedRevenue || 0,
          trailerRevenue: formData.trailerRevenue || 0,
          directRevenue: formData.directRevenue || 0,
          commissionRevenue: formData.commissionRevenue || 0,
          states: formData.states || [],
          clientDemographicsDetails: formData.clientDemographicsDetails || '',
          servicesProvidedDetails: formData.servicesProvidedDetails || ''
        }
      });

      if (data?.generatePartialSaleAiDescription) {
        let description = data.generatePartialSaleAiDescription.description || '';
        
        // Ensure description is under 250 characters
        if (description.length > 250) {
          description = _.truncate(description, {
            'length': 250,
            'separator': /[.!?]/,
            'omission': ''
          });
        }
        
        // Ensure description always ends with a period
        description = description.trim(); // Remove any trailing whitespace
        
        // Force proper ending - check if it ends with punctuation, if not add period
        if (!description.endsWith('.') && !description.endsWith('!') && !description.endsWith('?')) {
          description += '.';
        }
        
        setFormData({
          ...formData,
          segmentDetails: data.generatePartialSaleAiDescription.name || '',
          description: description
        });
      }
    } catch (error) {
      console.error('Error generating AI description:', error);
      alert(intl.formatMessage({ id: 'succession.partial_sales.description.error_ai_failed' }));
    }
  };

  const inputClasses = "w-full px-3 py-2 border border-succession-border rounded-lg focus:outline-none focus:ring-2 focus:ring-succession-primary focus:border-transparent transition-colors bg-succession-bg-primary text-succession-text-primary";
  const labelClasses = "block text-sm font-medium";
  const errorClasses = "mt-1 text-sm text-succession-error";

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold mb-4 text-succession-text-primary transition-colors duration-300">
          <FormattedMessage id="succession.partial_sales.description.title" />
        </h2>
        <p className="text-lg mb-6 text-succession-text-secondary transition-colors duration-300" style={{marginBottom: '0px'}}>
          <FormattedMessage id="succession.partial_sales.description.intro" />
        </p>
      </div>

      {/* AI Generation Button */}
      <div className="flex justify-end -mt-6" style={{marginTop: '0px'}}>
        <button
          onClick={handleGenerateAiDescription}
          disabled={aiLoading}
          className={`px-6 py-2 rounded-lg font-medium transition-colors ${
            aiLoading
              ? 'bg-succession-bg-secondary text-succession-text-muted cursor-not-allowed border border-succession-border'
              : 'bg-succession-primary text-white hover:opacity-80 transition-all'
          }`}
        >
          {aiLoading ? (
            <div className="flex items-center">
              <svg className="animate-spin h-4 w-4 mr-2" viewBox="0 0 24 24">
                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" className="opacity-25" />
                <path fill="currentColor" className="opacity-75" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
              <FormattedMessage id="succession.partial_sales.description.generating" />
            </div>
          ) : (
            intl.formatMessage({ id: 'succession.partial_sales.description.generate_with_ai' })
          )}
        </button>
      </div>

      {/* Segment Name */}
      <div>
        <label className={`${labelClasses} text-succession-text-primary transition-colors duration-300`} style={{paddingBottom: '0px', paddingTop: '0px'}}>
          <FormattedMessage id="succession.partial_sales.description.segment_name_label" />
        </label>
        <input
          type="text"
          value={formData.segmentDetails}
          onChange={(e) => handleInputChange('segmentDetails', e.target.value)}
          className={`${inputClasses} ${errors.segmentDetails ? 'border-succession-error' : ''}`}
          placeholder={intl.formatMessage({ id: 'succession.partial_sales.description.segment_name_placeholder' })}
          maxLength={100}
        />
        {errors.segmentDetails && (
          <p className={errorClasses}>{errors.segmentDetails}</p>
        )}
        <p className="mt-1 text-sm text-gray-500">
          {formData.segmentDetails?.length || 0}/100 characters
        </p>
      </div>

      {/* Description */}
      <div>
        <label className={`${labelClasses} text-succession-text-primary transition-colors duration-300`} style={{paddingBottom: '0px', paddingTop: '0px'}}>
          <FormattedMessage id="succession.partial_sales.description.description_label" />
        </label>
        <textarea
          value={formData.description}
          onChange={(e) => handleInputChange('description', e.target.value)}
          className={`${inputClasses} ${errors.description ? 'border-succession-error' : ''}`}
          rows={4}
          placeholder={intl.formatMessage({ id: 'succession.partial_sales.description.description_placeholder' })}
          maxLength={250}
        />
        {errors.description && (
          <p className={errorClasses}>{errors.description}</p>
        )}
        <p className="mt-1 text-sm text-gray-500">
          {formData.description?.length || 0}/250 characters
        </p>
      </div>

      {/* AI Privacy & Security */}
      <div className="bg-succession-bg-secondary border-l-4 border-orange-500 rounded-lg p-4 transition-colors duration-300">
        <p className="text-sm font-bold text-succession-text-primary mb-2">
          <FormattedMessage id="succession.partial_sales.description.ai_privacy_title" />
        </p>
        <p className="text-sm text-succession-text-secondary" style={{ lineHeight: '1.5' }}>
          <FormattedMessage id="succession.partial_sales.description.ai_privacy_body" />
        </p>
      </div>

      {/* Documentation Requirements — hidden where the tenant opted out (RJ). */}
      {!hidesListingGuidance(company) && (
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <h3 className="text-lg font-semibold text-yellow-900 mb-2">
          <FormattedMessage id="succession.partial_sales.description.documentation_title" />
        </h3>
        <p className="text-sm text-yellow-700">
          <FormattedMessage id="succession.partial_sales.description.documentation_intro" />
        </p>
        <ul className="mt-2 text-sm text-yellow-700 list-disc list-inside space-y-1">
          <li><FormattedMessage id="succession.partial_sales.description.doc_client_demographics" /></li>
          <li><FormattedMessage id="succession.partial_sales.description.doc_revenue_verification" /></li>
          <li><FormattedMessage id="succession.partial_sales.description.doc_asset_custody" /></li>
          <li><FormattedMessage id="succession.partial_sales.description.doc_compliance" /></li>
        </ul>
      </div>
      )}
    </div>
  );
};

export default Description;